import { useCrefisaContext } from '@/app/contexts/CrefisaContext'
import { crefisaService } from '@/app/services/crefisaService'
import { currentStep } from '@/app/store'
import { PartnerType } from '@/app/utils/partners.enum'
import { BasicInput } from '@/view/components/BasicInput'
import { BasicSelect } from '@/view/components/BasicSelect'
import { Button } from '@/view/components/ui/button'
import { Label } from '@/view/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/view/components/ui/radio-group'
import { useToast } from '@/view/components/ui/use-toast'
import { useQuery } from '@tanstack/react-query'
import { useCrefisaAction } from '@/app/hooks/useCrefisaStream'
import { StepProgress } from '../../components/StepProgress'
import { useAtom } from 'jotai'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

interface BankInfoFormProps {
  visible?: boolean
  onContinue: () => void
}

const FIXED_BANK = '104'
const FIXED_AGENCY = '38800'
const FIXED_ACCOUNT_TYPE = 'CP'

export const bankFormSchema = z.object({
  bank: z.string({ required_error: '* obrigatório' }).min(1, '* obrigatório'),
  agency: z.string().min(4, '* obrigatório').max(5, 'No máximo 5 caracteres'),
  account: z
    .string()
    .min(4, '* obrigatório')
    .max(11, 'No máximo 11 caracteres'),
  accountType: z.string().optional(),
})

const onlyDigitsInput = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.currentTarget
  target.value = target.value.replace(/\D/g, '')
}

export default function BankInfoForm({
  visible,
  onContinue,
}: BankInfoFormProps) {
  const [, setStep] = useAtom(currentStep);
  const {
    customerData,
    simulationData,
    setCustomer,
    partnerType,
    consentBankAccount,
  } = useCrefisaContext();

  const isInss = partnerType === PartnerType.CREFISA_INSS

  const { toast } = useToast()

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  const accountType = watch('bankAccount.accountType')

  const { data: bankOptions } = useQuery({
    queryKey: ['crefisa-complements'],
    queryFn: () => crefisaService.getComplements(),
    select: (data) =>
      data.bancosConveniados.map((bank) => ({
        label: bank.nome,
        value: String(bank.codigoBanco),
      })),
  })

  const {
    run: mutateAsync,
    isRunning: isPending,
    steps,
  } = useCrefisaAction((data: any, context) =>
    crefisaService.createClient(simulationData!.clientId!, data, context),
  )

  const onSubmit = async (data: any) => {
    try {
      const agencyValue = String(data.bankAccount?.agency ?? '')
      const accountValue = String(data.bankAccount?.account ?? '')

      const bankAccount = {
        bank: data.bankAccount?.bank ?? FIXED_BANK,
        accountType: data.bankAccount?.accountType ?? FIXED_ACCOUNT_TYPE,
        agency: agencyValue.slice(0, -1),
        agencyDigit: agencyValue.slice(-1),
        account: accountValue.slice(0, -1),
        accountDigit: accountValue.slice(-1),
      }

      const payload = {
        ...data.personalInfo,
        hasVisualImpairment: false,
        address: data.address,
        bankAccount,
        income: customerData?.income,
        type: simulationData?.type,
        ...(customerData?.dataConsentId
          ? { dataConsentId: customerData.dataConsentId }
          : {}),
      }

      const response = await mutateAsync(payload)

      if (!response.data.status) {
        toast({
          variant: 'destructive',
          title: 'Erro ao criar cliente',
          description:
            response?.data?.mensagem ||
            'Erro inesperado. Tente novamente mais tarde.',
        })
        return
      }

      setCustomer({
        ...payload,
        customerId: response!.customerId,
      })
      onContinue()
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar cliente',
        description:
          error?.message || 'Erro inesperado. Tente novamente mais tarde.',
      })
    }
  }

  useEffect(() => {
    if (isInss) {
      if (!accountType) {
        setValue('bankAccount.accountType', FIXED_ACCOUNT_TYPE)
      }
      return
    }
    setValue('bankAccount.bank', FIXED_BANK)
    setValue('bankAccount.agency', FIXED_AGENCY)
    setValue('bankAccount.accountType', FIXED_ACCOUNT_TYPE)
  }, [setValue, isInss, accountType])

  useEffect(() => {
    const bankAccount = consentBankAccount ?? customerData?.bankAccount;
    if (!bankAccount) return;

    if (bankAccount.bank) {
      setValue("bankAccount.bank", String(bankAccount.bank));
    }

    const agency = `${bankAccount.agency ?? ""}${bankAccount.agencyDigit ?? ""}`;
    if (agency) {
      setValue('bankAccount.agency', agency)
    }

    const account = `${bankAccount.account ?? ""}${bankAccount.accountDigit ?? ""}`;
    if (account) {
      setValue('bankAccount.account', account)
    }
  }, [consentBankAccount, customerData?.bankAccount, setValue]);

  return (
    <div className={visible ? 'flex flex-col gap-8' : 'hidden'}>
      {isInss && (
        <div className="grid w-full grid-cols-1 gap-y-2">
          <Label className="text-sm font-medium">Tipo de conta</Label>
          <RadioGroup
            value={accountType}
            onValueChange={(value) =>
              setValue('bankAccount.accountType', value)
            }
            className="flex gap-6"
          >
            <Label className="flex items-center gap-2 text-sm font-medium">
              <RadioGroupItem value="CP" />
              Poupança
            </Label>
            <Label className="flex items-center gap-2 text-sm font-medium">
              <RadioGroupItem value="CC" />
              Conta corrente
            </Label>
          </RadioGroup>
        </div>
      )}
      <div className="grid w-full grid-cols-1 gap-y-4">
        <BasicSelect
          name="bankAccount.bank"
          label="Banco"
          placeholder="Selecione o banco"
          options={bankOptions ?? []}
          error={(errors.bankAccount as any)?.bank?.message}
          disabled={!isInss}
        />
      </div>
      <div className="mr-8 grid w-full grid-cols-1 gap-4 md:grid-cols-2">
        <BasicInput
          tabIndex={0}
          name="bankAccount.agency"
          label="Agência com dígito"
          error={(errors.bankAccount as any)?.agency?.message}
          inputMode="numeric"
          maxLength={5}
          onInput={isInss ? onlyDigitsInput : undefined}
          disabled={!isInss}
        />
        <BasicInput
          tabIndex={0}
          name="bankAccount.account"
          label="Conta com dígito"
          error={(errors.bankAccount as any)?.account?.message}
          inputMode="numeric"
          maxLength={11}
          onInput={onlyDigitsInput}
        />
      </div>

      {isPending && steps.length > 0 && (
        <StepProgress steps={steps} title="Cadastrando o cliente" />
      )}

      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white"
          type="submit"
          isLoading={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          Continuar
        </Button>
      </div>
    </div>
  )
}
